import { useEffect, useRef, useState, type FormEvent } from 'react';
import { t } from '../../i18n/idioma';
import { GumpWindow } from '../GumpWindow';
import { ChatGump, LinhaDiario, type ConversaChat } from '../Journal';
import type { ChatLine, JournalLine } from '../../world/types';
import { ChatAtalhos } from './ChatAtalhos';
import { IconeHud } from './icones';
import { IcBalao } from '../icones/IconesV3';
import type { AbaChat } from './useChatNaoLidas';
import { useCompacto } from './useModoTela';

export function ChatHud({
  journal, local, combat, global, trade, guild, alliance, hasGuild, hasAlliance, net, onSay, focoSeq,
  aba, onAba, naoLidas, total, chatAberto, onAbrir, onFechar, min, onMin,
  conversas, conversa, onConversa, onFecharConversa, onTell, tellNotice
}: {
  journal: readonly JournalLine[];
  local: readonly ChatLine[];
  combat: readonly JournalLine[];
  global: readonly ChatLine[];
  trade: readonly ChatLine[];
  guild: readonly ChatLine[];
  alliance: readonly ChatLine[];
  hasGuild: boolean;
  hasAlliance: boolean;
  net: boolean;
  onSay?: (via: 0 | 1 | 2 | 4 | 5, text: string) => void;
  /** Enter no mundo: foca a fala local (aba Local no PC, campo da linha no celular). */
  focoSeq: number;
  aba: AbaChat;
  onAba(aba: AbaChat): void;
  naoLidas: Readonly<Record<string, number>>;
  total: number;
  chatAberto: boolean;
  onAbrir(): void;
  onFechar(): void;
  min: boolean;
  onMin(min: boolean): void;
  /** Conversas com amigos em abas (techspec-hud-v3 §23). */
  conversas: readonly ConversaChat[];
  conversa: string | null;
  onConversa(key: string): void;
  onFecharConversa(key: string): void;
  onTell(nome: string, texto: string): void;
  tellNotice: string | null;
}) {
  const compacto = useCompacto();
  const ultima = journal[journal.length - 1];
  /* minimizado: uma pílula com a última mensagem e o número do que não leu */
  const resumo = (
    <>
      <IcBalao />
      <span>{ultima ? <LinhaDiario line={ultima} /> : null}</span>
      {total > 0 && <i>{total}</i>}
      ▴
    </>
  );
  const corpo = (
    <ChatGump
      journal={journal}
      local={local}
      focoSeq={focoSeq}
      combat={combat}
      global={global}
      trade={trade}
      guild={guild}
      alliance={alliance}
      hasGuild={hasGuild}
      hasAlliance={hasAlliance}
      net={net}
      onSay={onSay}
      aba={aba}
      onAba={onAba}
      naoLidas={naoLidas}
      variante={compacto ? 'folha' : 'janela'}
      onMinimizar={() => onMin(true)}
      conversas={conversas}
      conversa={conversa}
      onConversa={onConversa}
      onFecharConversa={onFecharConversa}
      onTell={onTell}
      tellNotice={tellNotice}
    />
  );

  if (compacto) {
    if (!chatAberto) {
      return <FalarNaLinha local={local} total={total} onSay={onSay} onAbrir={onAbrir} focoSeq={focoSeq} />;
    }
    return (
      <GumpWindow
        title={t('hud.chat.titulo')}
        anchor={{}}
        className="pop ct ct--folha p-chat p-chat--folha"
        corpoClasse="p-chat__corpo"
        onClose={onFechar}
      >
        <ChatAtalhos folha />
        {corpo}
      </GumpWindow>
    );
  }

  /* PC: a placa das janelas, sem cabeçalho; arrasta pela linha das abas e a posição fica guardada.
     Minimizado, a pílula fica sempre no canto de baixo à esquerda (sem posição guardada); ao abrir, o chat
     volta para onde estava. As `key` diferentes fazem o React montar outra janela: sem elas ele reaproveitava
     a mesma e a pílula herdava o left/top que o arrasto deixou no estilo. */
  if (min) {
    return (
      <GumpWindow key="chat-min" title={t('diario.titulo')} anchor={{}} className="win--chat p-chat ct is-min" posicao={false} hud bare>
        <button type="button" className="ct-barra" aria-label={t('hud.chat.abrir')} onPointerDown={e => e.stopPropagation()} onClick={() => onMin(false)}>
          {resumo}
        </button>
      </GumpWindow>
    );
  }

  return (
    <GumpWindow key="chat" title={t('diario.titulo')} anchor={{}} className="win--chat p-chat ct" posicao="chat" hud bare>
      {/* Discord e Wiki presos à borda de cima do chat: vão junto no arrasto e somem com ele minimizado */}
      <ChatAtalhos />
      {corpo}
    </GumpWindow>
  );
}

/**
 * Celular: a janela do chat cobre o mundo, e ninguém veria o balão. A linha acima da barra vira
 * um campo de fala local; o ícone abre a conversa inteira. Fora de uso, mostra a última fala.
 */
let focoLinhaAtendido = 0;

function FalarNaLinha({ local, total, onSay, onAbrir, focoSeq }: {
  local: readonly ChatLine[];
  total: number;
  onSay?: (via: 0, text: string) => void;
  onAbrir(): void;
  focoSeq: number;
}) {
  const [texto, setTexto] = useState('');
  const campo = useRef<HTMLInputElement>(null);
  const ultima = local[local.length - 1];

  useEffect(() => {
    if (focoSeq <= focoLinhaAtendido) return;
    focoLinhaAtendido = focoSeq;
    campo.current?.focus();
  }, [focoSeq]);

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const limpo = texto.trim();
    if (!limpo || !onSay) return;
    onSay(0, limpo);
    setTexto('');
  };

  return (
    <form className="p-chat__linha p-chat__falar" onSubmit={enviar} data-nodrag>
      <button type="button" className="p-chat__abrir" aria-label={t('hud.chat.abrir')} title={t('hud.chat.abrir')} onClick={onAbrir}>
        <IconeHud nome="chat" />
        {total > 0 && <span className="kit-pip">{total}</span>}
      </button>
      <input
        ref={campo}
        type="text"
        maxLength={80}
        value={texto}
        onChange={e => setTexto(e.target.value)}
        placeholder={ultima ? `${ultima.name}: ${ultima.text}` : t('hud.chat.falarPerto')}
        aria-label={t('diario.chatLocal')}
        autoComplete="off"
        enterKeyHint="send"
        disabled={!onSay}
      />
      <button type="submit" className="btn btn--tiny" aria-label={t('hud.chat.enviar')} disabled={!onSay}>➤</button>
    </form>
  );
}
